import React, { useEffect, useMemo, useState, Suspense } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "../theme";
import { useAuth } from "../contexts/AuthContext";
import { useHomeRoute } from "../auth/useHomeRoute";
import Sidebar, { DRAWER_WIDTH } from "./Sidebar";
import Footer from "./Footer";
import Loading from "./Loading";
import { ROUTES_NAMES } from "./routes";
import {
  MiContenidoDashboardSkeleton,
  MiContenidoEntityDetailSkeleton,
} from "../miContenido/MiContenidoEntityDetailSkeleton";
import PodscopeTypingLoader from "./PodscopeTypingLoader";
import AuthPage from "../pages/AuthPage";
import AcceptCompanyInvitation from "../pages/AcceptCompanyInvitation";
import ForgotPassword from "../pages/ForgotPassword";
import ResetPassword from "../pages/ResetPassword";
import Explorer from "../pages/Explorer";
import PodcastDetail from "../pages/PodcastDetail";
import PodcastBrandMentions from "../pages/PodcastBrandMentions";
import Admin from "../pages/Admin";
import CompanyConfig from "../pages/CompanyConfig";
import MyPodcasts from "../pages/MyPodcasts";
import MyPodcastDetail from "../pages/MyPodcastDetail";
import { PublicSponsorLinkRouteSkeleton } from "../pages/PublicSponsorLinkRouteSkeleton";
import SponsorDetail from "../pages/SponsorDetail";
import Settings from "../pages/Settings";
import SettingsPodcastInvitations from "../pages/SettingsPodcastInvitations";
import InviteAcceptance from "../pages/InviteAcceptance";
import InviteStatesPreview from "../pages/invite/InviteStatesPreview";
import DevMetricsPdfPreview from "../pages/dev/DevMetricsPdfPreview";
import DevMetricsPdfPreviewWeb from "../pages/dev/DevMetricsPdfPreviewWeb";
import Elecciones from "../pages/Elecciones";
import BrandExplorer from "../pages/BrandExplorer";
import BrandDetail from "../pages/BrandDetail";
import PodcastAnalysisReview from "../pages/PodcastAnalysisReview";
import PodcastContentAnalysis from "../pages/PodcastContentAnalysis";
import ContentTaxonomyTopics from "../pages/ContentTaxonomyTopics";
import PodcastTaxonomyTopics from "../pages/PodcastTaxonomyTopics";
import ProgramAnalysisReview from "../pages/ProgramAnalysisReview";
import PodcastLearningsExplorer from "../pages/PodcastLearningsExplorer";
import PgmAuditListPage from "../pages/PgmAuditListPage";
import PgmAuditDetailPage from "../pages/PgmAuditDetailPage";
import ProgramDailyMetricsPage from "../pages/ProgramDailyMetricsPage";
import ProgramAnalysisViewsAssetRender from "../pages/ProgramAnalysisViewsAssetRender";
import ProgramAnalysisRankingAsset from "../pages/ProgramAnalysisRankingAsset";
import ProgramAnalysisRankingAssetRender from "../pages/ProgramAnalysisRankingAssetRender";
import ProgramAnalysisLinkedInAssetRender from "../pages/ProgramAnalysisLinkedInAssetRender";
import ProgramAnalysisLimited from "../pages/ProgramAnalysisLimited";
import SocialAdmin from "../pages/SocialAdmin";
import ChannelAnalysisAdmin from "../pages/ChannelAnalysisAdmin";
import BrandAnalysisAdmin from "../pages/BrandAnalysisAdmin";
import ProgramAnalysisAdmin from "../pages/ProgramAnalysisAdmin";
import MediaChannelsExplorer from "../pages/MediaChannelsExplorer";
import {
  LegacyMediaChannelsRedirect,
  MediaChannelDetailPage,
} from "../pages/MediaChannelDetailPage";
import PipelinesAdmin from "../pages/PipelinesAdmin";
import AdminPipelineDigestPreview from "../pages/AdminPipelineDigestPreview";
import AdminEmailing from "../pages/AdminEmailing";
import AdminMetricsReports from "../pages/AdminMetricsReports";
import ReviewAuditAdmin from "../pages/ReviewAuditAdmin";
import ReviewAuditCases from "../pages/ReviewAuditCases";
import ChatUsageAdmin from "../pages/ChatUsageAdmin";
import ReviewTimeTracker from "./ReviewTimeTracker";
import ExplorerProgramDetail from "../pages/ExplorerProgramDetail";
import ExplorerPodcastDetail from "../pages/ExplorerPodcastDetail";
import ExplorerSocialChannelDetail from "../pages/Explorer/ExplorerSocialChannelDetail";
import ExplorerSocialBrandDetail from "../pages/Explorer/ExplorerSocialBrandDetail";
import ExplorerLivestreamChannelDetail from "../pages/ExplorerLivestreamChannelDetail";
import ExplorerLivestreamProgramDetail from "../pages/ExplorerLivestreamProgramDetail";
import LivestreamsHeatmapRedirect from "../pages/Explorer/LivestreamsHeatmapRedirect";
import ExplorerBrandDetail from "../pages/ExplorerBrandDetail";
import ExplorerBrandMovers from "../pages/ExplorerBrandMovers";
import BrandExplorerYear from "../pages/BrandExplorerYear";
import PrivacyPolicy from "../pages/legal/PrivacyPolicy";
import TermsOfService from "../pages/legal/TermsOfService";
import PodcastMediaKit from "../pages/PodcastMediaKit";
import AdminPrixiCompanies from "../pages/AdminPrixiCompanies";
import BrandQualitativeReport from "../pages/BrandQualitativeReport";
import PapiRickyGoogleSearch from "../pages/PapiRickyGoogleSearch";

import ProtectedRoute from "./ProtectedRoute";
import AdminRoute from "./AdminRoute";
import MiContenidoDemoRoute from "./MiContenidoDemoRoute";
import ExplorerRoute from "./ExplorerRoute";
import MisPodcastsRoute from "./MisPodcastsRoute";
import MiContenidoRoute from "./MiContenidoRoute";
import ClipsRoute from "./ClipsRoute";
import CampaignRoute from "./CampaignRoute";
import TrackerRoute from "./TrackerRoute";
import BrandExplorerProgramDetail from "../pages/BrandExplorerProgramDetail";
import TrackerLivestreamProgramDetail from "../pages/TrackerLivestreamProgramDetail";
import BrandExplorerCreatorDetail from "../pages/BrandExplorerCreatorDetail";
import BrandExplorerContentDetail from "../pages/BrandExplorerContentDetail";
import BrandExplorerShortFormPlatformDetail from "../pages/BrandExplorerShortFormPlatformDetail";
import BrandExplorerYoutubeShortsCreatorDetail from "../pages/BrandExplorerYoutubeShortsCreatorDetail";

const ExplorerProduct = React.lazy(() => import("../pages/ExplorerProduct"));
const ExplorerMisProgramasPage = React.lazy(
  () => import("../pages/Explorer/ExplorerMisProgramasPage"),
);
const CreateExplorerProgramPage = React.lazy(
  () => import("../pages/Explorer/CreateExplorerProgramPage"),
);
const ExplorerFollowedProgramFeedPage = React.lazy(
  () => import("../pages/Explorer/ExplorerFollowedProgramFeedPage"),
);
const ChatPage = React.lazy(() => import("../pages/Chat/ChatPage"));

const LegacyInicioRedirect: React.FC = () => {
  const { chatId } = useParams();
  return <Navigate to={chatId ? `${ROUTES_NAMES.CHAT}/${chatId}` : ROUTES_NAMES.CHAT} replace />;
};

const LegacyExplorerSocialRedirect: React.FC = () => {
  const { channelId } = useParams();
  const { search } = useLocation();
  const path = channelId
    ? `${ROUTES_NAMES.EXPLORER_SOCIAL_LIST}/${channelId}`
    : ROUTES_NAMES.EXPLORER_SOCIAL_LIST;
  return <Navigate to={`${path}${search}`} replace />;
};
const ProfilePage = React.lazy(() => import("../pages/ProfilePage"));
const MiContenidoDemoPage = React.lazy(
  () => import("../pages/MiContenidoDemoPage"),
);
const MiContenidoDemoCollectionReportPage = React.lazy(
  () => import("../pages/MiContenidoDemoCollectionReportPage"),
);
const GroupDetail = React.lazy(() => import("../pages/GroupDetail"));
const PublicCollectionReportGate = React.lazy(
  () => import("../pages/clips/PublicCollectionReportGate"),
);
const CreateCollection = React.lazy(() => import("../pages/CreateCollection"));
const PlatformContentPage = React.lazy(
  () => import("../pages/PlatformContentPage"),
);
const ProfileProgramDetailPage = React.lazy(
  () => import("../pages/ProfileProgramDetailPage"),
);
const ProfileBrandDetailPage = React.lazy(
  () => import("../pages/ProfileBrandDetailPage"),
);
const ProfileChannelDetailPage = React.lazy(
  () => import("../pages/ProfileChannelDetailPage"),
);
const ProfilePodcastDetailPage = React.lazy(
  () => import("../pages/ProfilePodcastDetailPage"),
);
const MiContenidoEntityMetricsDetailPage = React.lazy(
  () => import("../miContenido/MiContenidoEntityMetricsDetailPage"),
);
const MiContenidoEntityReportsPage = React.lazy(
  () => import("../miContenido/MiContenidoEntityReportsPage"),
);
const MiContenidoEntityContentDetailPage = React.lazy(
  () => import("../pages/MiContenidoEntityContentDetailPage"),
);
const MiContenidoPodcastTopicsPage = React.lazy(
  () => import("../pages/MiContenidoPodcastTopicsPage"),
);
const MiContenidoColeccionesPage = React.lazy(
  () => import("../pages/MiContenidoColeccionesPage"),
);
const MiContenidoMediaKitsPage = React.lazy(
  () => import("../pages/MiContenidoMediaKitsPage"),
);
const MiContenidoMediaKitDetailPage = React.lazy(
  () => import("../pages/MiContenidoMediaKitDetailPage"),
);
const ClipsPage = React.lazy(() => import("../pages/clips/ClipsPage"));
const ClipsCreatePage = React.lazy(() => import("../pages/clips/ClipsCreatePage"));
const ClipsDetailPage = React.lazy(() => import("../pages/clips/ClipsDetailPage"));
const CampaignsPage = React.lazy(() => import("../pages/CampaignsPage"));
const CampaignDetailPage = React.lazy(
  () => import("../pages/CampaignDetailPage"),
);
const CampaignContentDetailPage = React.lazy(
  () => import("../pages/CampaignContentDetailPage"),
);
const CampaignPlatformContentPage = React.lazy(
  () => import("../pages/CampaignPlatformContentPage"),
);
const CampaignMetricsDetailPage = React.lazy(
  () => import("../pages/CampaignMetricsDetailPage"),
);
const CampaignLinksDetailPage = React.lazy(
  () => import("../pages/CampaignLinksDetailPage"),
);
// Public route wrapper for TonyBet Explorer (no authentication required, fixed brand ID)
const PublicTonyBetExplorerRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <>{children}</>;
};

// Custom route wrapper for Limited Program Analysis (allows specific user only)
const LimitedProgramAnalysisRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: "50vh",
        }}
      >
        <Loading message="Validando sesión..." />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES_NAMES.AUTH} replace />;
  }

  // Check if user is the specific user allowed to access
  const hasAccess = user?._id === "6914dccdb3291e1d80ebfe0a";
  if (!hasAccess) {
    return <Navigate to={ROUTES_NAMES.MIS_PODCASTS} replace />;
  }

  return <>{children}</>;
};

const RedirectViewsAssetToRanking: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("ranking", "views");
  return (
    <Navigate
      to={`${ROUTES_NAMES.PROGRAM_ANALYSIS_RANKING_ASSET}?${params.toString()}`}
      replace
    />
  );
};

/** Waits for company permissions and chat access, then sends users to their home route (also used post-login). */
const AuthenticatedDefaultRedirect: React.FC = () => {
  const { isLoading, isProductPermissionsReady } = useAuth();
  const { route, isLoading: homeRouteLoading } = useHomeRoute();

  if (isLoading || !isProductPermissionsReady || homeRouteLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: { xs: "50vh", md: "65vh" },
          width: "100%",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const to = route === ROUTES_NAMES.AUTH ? ROUTES_NAMES.MIS_PODCASTS : route;

  return <Navigate to={to} replace />;
};

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { isLoading, isAuthenticated, isProductPermissionsReady } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isProgramAssetRenderPath =
    location.pathname === ROUTES_NAMES.PROGRAM_ANALYSIS_VIEWS_ASSET_RENDER ||
    location.pathname === ROUTES_NAMES.PROGRAM_ANALYSIS_RANKING_ASSET_RENDER ||
    location.pathname === ROUTES_NAMES.PROGRAM_ANALYSIS_LINKEDIN_ASSET_RENDER;
  const isEleccionesPage = location.pathname === ROUTES_NAMES.ELECCIONES;
  const eleccionesTheme = useMemo(() => createAppTheme("dark"), []);

  useEffect(() => {
    if (!isEleccionesPage) return;
    const { body } = document;
    const root = document.documentElement;
    const previousBackground = body.style.backgroundColor;
    const previousScheme = root.style.colorScheme;
    body.style.backgroundColor = "#08070B";
    root.style.colorScheme = "dark";
    return () => {
      body.style.backgroundColor = previousBackground;
      root.style.colorScheme = previousScheme;
    };
  }, [isEleccionesPage]);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Bare render routes for PNG export / Puppeteer — no sidebar or app chrome.
  if (isProgramAssetRenderPath) {
    return (
      <Routes>
        <Route
          path={ROUTES_NAMES.PROGRAM_ANALYSIS_VIEWS_ASSET_RENDER}
          element={<ProgramAnalysisViewsAssetRender />}
        />
        <Route
          path={ROUTES_NAMES.PROGRAM_ANALYSIS_RANKING_ASSET_RENDER}
          element={<ProgramAnalysisRankingAssetRender />}
        />
        <Route
          path={ROUTES_NAMES.PROGRAM_ANALYSIS_LINKEDIN_ASSET_RENDER}
          element={<ProgramAnalysisLinkedInAssetRender />}
        />
      </Routes>
    );
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Show sidebar for explorar, podcasts, mis-podcasts, settings, admin, temp, podcast-analysis, program-analysis, brand-strategy, social, and explorer routes (excluding elecciones and public tonybet explorer)
  const isMiContenidoDemoCollectionReport =
    location.pathname === ROUTES_NAMES.MI_CONTENIDO_DEMO_COLLECTION ||
    location.pathname.startsWith(
      `${ROUTES_NAMES.MI_CONTENIDO_DEMO_COLLECTION}/`,
    );
  const showSidebar =
    !location.pathname.startsWith("/tonybet") &&
    !location.pathname.startsWith("/reportes/marcas/") &&
    !isMiContenidoDemoCollectionReport &&
    (location.pathname.startsWith(ROUTES_NAMES.CHAT) ||
      location.pathname.startsWith(ROUTES_NAMES.EXPLORAR) ||
      location.pathname.startsWith(ROUTES_NAMES.PODCASTS) ||
      location.pathname.startsWith(ROUTES_NAMES.MIS_PODCASTS) ||
      location.pathname.startsWith(ROUTES_NAMES.MI_CONTENIDO) ||
      location.pathname.startsWith(ROUTES_NAMES.SETTINGS) ||
      location.pathname.startsWith(ROUTES_NAMES.ADMIN) ||
      location.pathname === ROUTES_NAMES.CHANNELS ||
      location.pathname.startsWith(`${ROUTES_NAMES.CHANNELS}/`) ||
      location.pathname.startsWith(ROUTES_NAMES.TEMP) ||
      location.pathname.startsWith(ROUTES_NAMES.BRAND_ANALYSIS) ||
      location.pathname.startsWith(ROUTES_NAMES.PODCAST_ANALYSIS) ||
      location.pathname.startsWith(ROUTES_NAMES.PROGRAM_ANALYSIS) ||
      location.pathname.startsWith(ROUTES_NAMES.PROGRAM_ANALYSIS_LIMITED) ||
      location.pathname.startsWith(ROUTES_NAMES.SOCIAL_ADMIN) ||
      location.pathname.startsWith(ROUTES_NAMES.EXPLORER) ||
      location.pathname.startsWith(ROUTES_NAMES.TRACKER) ||
      location.pathname.startsWith("/clips") ||
      location.pathname.startsWith(ROUTES_NAMES.CAMPANAS));

  // Auth shell: sign-in, invite accept, forgot/reset password (no sidebar)
  const isAuthFlowPage =
    location.pathname === ROUTES_NAMES.AUTH ||
    location.pathname.startsWith(`${ROUTES_NAMES.AUTH}/`);

  /** Podcast/social invite: /:companySlug/invitacion/:inviteToken — same flex shell as /auth* so footer stays in flow */
  const isPodcastInvitePage = /^\/[^/]+\/invitacion\/[^/]+$/.test(
    location.pathname,
  );
  const isDevInviteStatesPage =
    process.env.NODE_ENV === "development" &&
    location.pathname === ROUTES_NAMES.DEV_INVITE_STATES;
  const isAuthLayoutShell =
    isAuthFlowPage || isPodcastInvitePage || isDevInviteStatesPage;

  const isLegalPage = location.pathname.startsWith("/legal/");

  // Check if current route should show footer (exclude public sponsor links)
  const shouldShowFooter = isEleccionesPage || isLegalPage;

  // Check if current route is a public sponsor link or media kit (public route without sidebar)
  const isPublicSponsorLink = location.pathname.match(
    /^\/[^/]+\/[^/]+\/brands\/[^/]+$/,
  );
  const isPublicCollectionView = /^\/colecciones\/[^/]+$/.test(
    location.pathname,
  );
  const isPublicTonyBetExplorer = location.pathname.startsWith("/tonybet");
  const isPublicBrandReport = location.pathname.startsWith("/reportes/marcas/");
  const isPapiRickyFeed = location.pathname === ROUTES_NAMES.PAPI_RICKY;

  // Check if current route is any public route
  const isPublicRoute =
    isEleccionesPage ||
    isLegalPage ||
    isPublicSponsorLink ||
    isPublicCollectionView ||
    isPublicTonyBetExplorer ||
    isPublicBrandReport ||
    isPapiRickyFeed;

  // Full-bleed chrome (no sidebar padding) without skipping auth bootstrap — demo report requires login.
  const isFullBleedAuthenticatedReport = isMiContenidoDemoCollectionReport;

  const skipSessionBootstrapLoader =
    isPublicRoute || isPodcastInvitePage || isDevInviteStatesPage;

  const isSessionBootstrapPending =
    isLoading || (isAuthenticated && !isProductPermissionsReady);
  const showAppChrome = showSidebar && !isSessionBootstrapPending;

  const showReviewTimeTracker = location.pathname.startsWith(
    ROUTES_NAMES.PODCAST_ANALYSIS,
  );

  const layoutTree = (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        bgcolor: "background.default",
      }}
    >
      {showReviewTimeTracker && <ReviewTimeTracker />}

      {/* Sidebar */}
      {showAppChrome && (
        <Sidebar
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
        />
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          width: {
            xs: "100%",
            md: showAppChrome ? `calc(100% - ${DRAWER_WIDTH}px)` : "100%",
          },
          minWidth: 0,
          bgcolor: "background.default",
          ml: { xs: 0, md: showAppChrome ? `${DRAWER_WIDTH}px` : 0 },
        }}
      >
        {/* Custom Top Bar for Mobile */}
        {showAppChrome && isMobile && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              height: "64px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "background.paper",
              borderBottom: "1px solid",
              borderColor: "divider",
              zIndex: theme.zIndex.drawer - 1,
              px: 2,
            }}
          >
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                color: "text.primary",
                p: 1.5,
                borderRadius: 2,
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Typography variant="body2" sx={{ textDecoration: "underline" }}>
                Menú
              </Typography>
            </IconButton>
            <Typography
              component="h1"
              variant="h1"
              sx={{
                margin: 0,
                fontSize: "1.5rem",
                fontWeight: 800,
                color: "primary.main",
              }}
            >
              Podscope
            </Typography>
            <Box sx={{ width: 60 }} /> {/* Spacer to center the text */}
          </Box>
        )}

        {/* Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            ...(isAuthLayoutShell && {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }),
            pt: {
              xs: showAppChrome && isMobile ? 8 : 0,
              md: isPublicRoute || isFullBleedAuthenticatedReport ? 0 : 2,
            },
            // px: isAuthPage ? 0 : showSidebar ? 3 : 0,
            pb:
              isAuthLayoutShell
                ? 0
                : showAppChrome
                  ? 3
                  : isPublicRoute || isFullBleedAuthenticatedReport
                    ? 0
                    : 0,
            width: "100%",
            // maxWidth: isAuthPage ? '100%' : showSidebar ? '1290px' : '100%',
            mx: "auto",
            minWidth: 0,
            px: { xs: 2, md: 0 },
            mt: { xs: showAppChrome && isMobile ? 1 : 0, md: 0 },
            bgcolor: "background.default",
          }}
        >
          {isSessionBootstrapPending && !skipSessionBootstrapLoader ? (
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                minHeight: "100vh",
              }}
            >
              <PodscopeTypingLoader />
            </Box>
          ) : (
            <Routes>
              <Route
                path={ROUTES_NAMES.AUTH_INVITACION}
                element={<AcceptCompanyInvitation />}
              />
              <Route
                path={ROUTES_NAMES.AUTH_FORGOT_PASSWORD}
                element={<ForgotPassword />}
              />
              <Route
                path={ROUTES_NAMES.AUTH_RESET_PASSWORD}
                element={<ResetPassword />}
              />
              <Route
                path={ROUTES_NAMES.AUTH}
                element={
                  isAuthenticated &&
                  !new URLSearchParams(location.search).has("demo") ? (
                    <AuthenticatedDefaultRedirect />
                  ) : (
                    <AuthPage />
                  )
                }
              />
              <Route
                path={ROUTES_NAMES.ROOT}
                element={
                  isAuthenticated ? (
                    <AuthenticatedDefaultRedirect />
                  ) : (
                    <Navigate to={ROUTES_NAMES.AUTH} replace />
                  )
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORAR}
                element={
                  <ProtectedRoute>
                    <Explorer />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PODCASTS}
                element={
                  <ProtectedRoute>
                    <Explorer />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MIS_PODCASTS}
                element={
                  <MisPodcastsRoute>
                    <MyPodcasts />
                  </MisPodcastsRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CHAT_THREAD}
                element={
                  <Suspense fallback={<Loading message="Cargando..." />}>
                    <ChatPage />
                  </Suspense>
                }
              />
              <Route path={ROUTES_NAMES.LEGACY_INICIO} element={<LegacyInicioRedirect />} />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO}
                element={
                  <MiContenidoRoute>
                    <Suspense fallback={<MiContenidoDashboardSkeleton />}>
                      <ProfilePage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_PROGRAMA_REPORTES}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={
                        <MiContenidoEntityDetailSkeleton variant="metricsDetail" />
                      }
                    >
                      <MiContenidoEntityReportsPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_PROGRAMA_METRICAS}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={
                        <MiContenidoEntityDetailSkeleton variant="metricsDetail" />
                      }
                    >
                      <MiContenidoEntityMetricsDetailPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_PROGRAMA_CONTENIDO}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={
                        <MiContenidoEntityDetailSkeleton variant="contentDetail" />
                      }
                    >
                      <MiContenidoEntityContentDetailPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_PROGRAMA_DETAIL}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={
                        <MiContenidoEntityDetailSkeleton variant="program" />
                      }
                    >
                      <ProfileProgramDetailPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_PODCAST_REPORTES}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={
                        <MiContenidoEntityDetailSkeleton variant="metricsDetail" />
                      }
                    >
                      <MiContenidoEntityReportsPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_PODCAST_METRICAS}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={
                        <MiContenidoEntityDetailSkeleton variant="metricsDetail" />
                      }
                    >
                      <MiContenidoEntityMetricsDetailPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_PODCAST_CONTENIDO}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={
                        <MiContenidoEntityDetailSkeleton variant="contentDetail" />
                      }
                    >
                      <MiContenidoEntityContentDetailPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_PODCAST_TOPICS}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={
                        <MiContenidoEntityDetailSkeleton
                          variant="podcast"
                          metricsTitle="Temas"
                        />
                      }
                    >
                      <MiContenidoPodcastTopicsPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_PODCAST_DETAIL}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={
                        <MiContenidoEntityDetailSkeleton
                          variant="podcast"
                          metricsTitle="Métricas del canal"
                        />
                      }
                    >
                      <ProfilePodcastDetailPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_CANAL_METRICAS}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={
                        <MiContenidoEntityDetailSkeleton variant="metricsDetail" />
                      }
                    >
                      <MiContenidoEntityMetricsDetailPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_CANAL_CONTENIDO}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={
                        <MiContenidoEntityDetailSkeleton variant="contentDetail" />
                      }
                    >
                      <MiContenidoEntityContentDetailPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_CANAL_DETAIL}
                element={
                  <MiContenidoRoute>
                    <Suspense fallback={<Loading />}>
                      <ProfileChannelDetailPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_MARCA_DETAIL}
                element={
                  <MiContenidoRoute>
                    <Suspense
                      fallback={<MiContenidoEntityDetailSkeleton variant="brand" />}
                    >
                      <ProfileBrandDetailPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CAMPANAS_LINKS}
                element={
                  <CampaignRoute>
                    <Suspense fallback={<Loading />}>
                      <CampaignLinksDetailPage />
                    </Suspense>
                  </CampaignRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CAMPANAS_METRICAS}
                element={
                  <CampaignRoute>
                    <Suspense fallback={<Loading />}>
                      <CampaignMetricsDetailPage />
                    </Suspense>
                  </CampaignRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CAMPANAS_PLATFORM}
                element={
                  <CampaignRoute>
                    <Suspense fallback={<Loading />}>
                      <CampaignPlatformContentPage />
                    </Suspense>
                  </CampaignRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CAMPANAS_CONTENT}
                element={
                  <CampaignRoute>
                    <Suspense fallback={<Loading />}>
                      <CampaignContentDetailPage />
                    </Suspense>
                  </CampaignRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CAMPANAS_DETAIL}
                element={
                  <CampaignRoute>
                    <Suspense fallback={<Loading />}>
                      <CampaignDetailPage />
                    </Suspense>
                  </CampaignRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CAMPANAS}
                element={
                  <CampaignRoute>
                    <Suspense fallback={<Loading />}>
                      <CampaignsPage />
                    </Suspense>
                  </CampaignRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_COLECCIONES}
                element={
                  <MiContenidoRoute>
                    <Suspense fallback={<Loading />}>
                      <MiContenidoColeccionesPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_MEDIA_KITS}
                element={
                  <MiContenidoRoute>
                    <Suspense fallback={<Loading />}>
                      <MiContenidoMediaKitsPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_MEDIA_KIT_DETAIL}
                element={
                  <MiContenidoRoute>
                    <Suspense fallback={<Loading />}>
                      <MiContenidoMediaKitDetailPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_CREAR_COLECCION}
                element={
                  <MiContenidoRoute>
                    <Suspense fallback={<Loading />}>
                      <CreateCollection />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_COLECCION_DETAIL}
                element={
                  <MiContenidoRoute>
                    <Suspense fallback={<Loading />}>
                      <GroupDetail />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_DEMO}
                element={
                  <MiContenidoDemoRoute>
                    <Suspense fallback={<Loading />}>
                      <MiContenidoDemoPage />
                    </Suspense>
                  </MiContenidoDemoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_DEMO_COLLECTION}
                element={
                  <MiContenidoDemoRoute>
                    <Suspense fallback={<Loading />}>
                      <MiContenidoDemoCollectionReportPage />
                    </Suspense>
                  </MiContenidoDemoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MI_CONTENIDO_PLATFORM}
                element={
                  <MiContenidoRoute>
                    <Suspense fallback={<Loading />}>
                      <PlatformContentPage />
                    </Suspense>
                  </MiContenidoRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CLIPS}
                element={
                  <ClipsRoute>
                    <Suspense fallback={<Loading />}>
                      <ClipsPage />
                    </Suspense>
                  </ClipsRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CLIPS_NUEVA}
                element={
                  <ClipsRoute>
                    <Suspense fallback={<Loading />}>
                      <ClipsCreatePage />
                    </Suspense>
                  </ClipsRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CLIPS_DETAIL}
                element={
                  <ClipsRoute>
                    <Suspense fallback={<Loading />}>
                      <ClipsDetailPage />
                    </Suspense>
                  </ClipsRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MIS_PODCASTS_MEDIA_KITS}
                element={
                  <MisPodcastsRoute>
                    <Suspense fallback={<Loading />}>
                      <MiContenidoMediaKitsPage />
                    </Suspense>
                  </MisPodcastsRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MIS_PODCASTS_MEDIA_KIT_DETAIL}
                element={
                  <MisPodcastsRoute>
                    <Suspense fallback={<Loading />}>
                      <MiContenidoMediaKitDetailPage />
                    </Suspense>
                  </MisPodcastsRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.MY_PODCAST_DETAIL}
                element={
                  <MisPodcastsRoute>
                    <MyPodcastDetail />
                  </MisPodcastsRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.COLLECTION_DETAIL}
                element={
                  <MisPodcastsRoute>
                    <Suspense fallback={<Loading />}>
                      <GroupDetail />
                    </Suspense>
                  </MisPodcastsRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CREATE_COLLECTION}
                element={
                  <MisPodcastsRoute>
                    <Suspense fallback={<Loading />}>
                      <CreateCollection />
                    </Suspense>
                  </MisPodcastsRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.INVITE}
                element={<InviteAcceptance />}
              />
              <Route
                path={ROUTES_NAMES.MEDIA_KIT}
                element={
                  <Suspense fallback={<PublicSponsorLinkRouteSkeleton />}>
                    <PublicCollectionReportGate />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES_NAMES.PUBLIC_SPONSOR_LINK}
                element={
                  <Suspense fallback={<PublicSponsorLinkRouteSkeleton />}>
                    <PublicCollectionReportGate />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES_NAMES.PUBLIC_SPONSOR_LINK_LEGACY}
                element={
                  <Suspense fallback={<PublicSponsorLinkRouteSkeleton />}>
                    <PublicCollectionReportGate />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES_NAMES.PROGRAM_MEDIA_KIT_BY_PATH}
                element={<PodcastMediaKit />}
              />
              <Route
                path={ROUTES_NAMES.PROGRAM_MEDIA_KIT}
                element={<PodcastMediaKit />}
              />
              <Route
                path={ROUTES_NAMES.COMPANY_MEDIA_KIT_EXAMPLE}
                element={<PodcastMediaKit />}
              />
              <Route
                path={ROUTES_NAMES.PODCAST_MEDIA_KIT}
                element={<PodcastMediaKit />}
              />
              <Route
                path={ROUTES_NAMES.PODCAST_MEDIA_KIT_SHORT}
                element={<PodcastMediaKit />}
              />
              <Route
                path={ROUTES_NAMES.PODCAST_DETAIL}
                element={
                  <ProtectedRoute>
                    <PodcastDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PODCAST_BRAND_MENTIONS}
                element={
                  <ProtectedRoute>
                    <PodcastBrandMentions />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.SPONSOR_DETAIL}
                element={
                  <ProtectedRoute>
                    <SponsorDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.ADMIN}
                element={
                  <AdminRoute>
                    <Admin />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.ADMIN_PRIXI_COMPANIES}
                element={
                  <AdminRoute>
                    <AdminPrixiCompanies />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.SETTINGS}
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.SETTINGS_PODCAST}
                element={
                  <ProtectedRoute>
                    <SettingsPodcastInvitations />
                  </ProtectedRoute>
                }
              />
              <Route path={ROUTES_NAMES.ELECCIONES} element={<Elecciones />} />
              <Route path={ROUTES_NAMES.PAPI_RICKY} element={<PapiRickyGoogleSearch />} />
              <Route
                path={ROUTES_NAMES.TEMP}
                element={
                  <ProtectedRoute>
                    <BrandExplorer />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.BRAND_ANALYSIS}
                element={
                  <AdminRoute>
                    <BrandAnalysisAdmin />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PODCAST_ANALYSIS}
                element={
                  <AdminRoute>
                    <PodcastAnalysisReview />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PODCAST_CONTENT_ANALYSIS}
                element={
                  <AdminRoute>
                    <PodcastContentAnalysis />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PODCAST_TAXONOMY_TOPICS}
                element={
                  <AdminRoute>
                    <PodcastTaxonomyTopics />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CONTENT_TAXONOMY_TOPICS}
                element={
                  <AdminRoute>
                    <ContentTaxonomyTopics />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PROGRAM_ANALYSIS}
                element={
                  <AdminRoute>
                    <ProgramAnalysisReview />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.ADMIN_PROGRAM_ANALYSIS}
                element={
                  <AdminRoute>
                    <ProgramAnalysisAdmin />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CHANNEL_ANALYSIS}
                element={
                  <AdminRoute>
                    <ChannelAnalysisAdmin />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CHANNELS}
                element={
                  <AdminRoute>
                    <MediaChannelsExplorer />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.CHANNEL_DETAIL}
                element={
                  <AdminRoute>
                    <MediaChannelDetailPage />
                  </AdminRoute>
                }
              />
              <Route path="/admin/media-channels" element={<LegacyMediaChannelsRedirect />} />
              <Route
                path={ROUTES_NAMES.PODCAST_LEARNINGS_EXPLORER}
                element={
                  <AdminRoute>
                    <PodcastLearningsExplorer />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PGM_AUDIT_DETAIL}
                element={
                  <AdminRoute>
                    <PgmAuditDetailPage />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PGM_AUDIT}
                element={
                  <AdminRoute>
                    <PgmAuditListPage />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PROGRAM_ANALYSIS_VIEWS_ASSET}
                element={
                  <AdminRoute>
                    <RedirectViewsAssetToRanking />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PROGRAM_ANALYSIS_RANKING_ASSET}
                element={
                  <AdminRoute>
                    <ProgramAnalysisRankingAsset />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PROGRAM_ANALYSIS_DETAIL}
                element={
                  <AdminRoute>
                    <ProgramDailyMetricsPage />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.PROGRAM_ANALYSIS_LIMITED}
                element={
                  <LimitedProgramAnalysisRoute>
                    <ProgramAnalysisLimited />
                  </LimitedProgramAnalysisRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.BRAND_DETAIL}
                element={
                  <AdminRoute>
                    <BrandDetail />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.ADMIN_COMPANY_CONFIG}
                element={
                  <AdminRoute>
                    <CompanyConfig />
                  </AdminRoute>
                }
              />
              {process.env.NODE_ENV === "development" && (
                <Route
                  path={ROUTES_NAMES.DEV_INVITE_STATES}
                  element={<InviteStatesPreview />}
                />
              )}
              {process.env.NODE_ENV === "development" && (
                <Route
                  path={ROUTES_NAMES.DEV_METRICS_PDF_PREVIEW}
                  element={
                    <ProtectedRoute>
                      <DevMetricsPdfPreview />
                    </ProtectedRoute>
                  }
                />
              )}
              {process.env.NODE_ENV === "development" && (
                <Route
                  path={ROUTES_NAMES.DEV_METRICS_PDF_PREVIEW_WEB}
                  element={
                    <ProtectedRoute>
                      <DevMetricsPdfPreviewWeb />
                    </ProtectedRoute>
                  }
                />
              )}
              <Route
                path={ROUTES_NAMES.SOCIAL_ADMIN}
                element={
                  <AdminRoute>
                    <SocialAdmin />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.ADMIN_PIPELINES}
                element={
                  <AdminRoute>
                    <PipelinesAdmin />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.ADMIN_PIPELINE_DIGEST_PREVIEW}
                element={
                  <AdminRoute>
                    <AdminPipelineDigestPreview />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.ADMIN_EMAILING}
                element={
                  <AdminRoute>
                    <AdminEmailing />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.ADMIN_METRICS_REPORTS}
                element={
                  <AdminRoute>
                    <AdminMetricsReports />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.ADMIN_REVIEW_AUDIT}
                element={
                  <AdminRoute>
                    <ReviewAuditAdmin />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.ADMIN_REVIEW_AUDIT_CASES}
                element={
                  <AdminRoute>
                    <ReviewAuditCases />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.ADMIN_CHAT_USAGE}
                element={
                  <AdminRoute>
                    <ChatUsageAdmin />
                  </AdminRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER}
                element={
                  <ExplorerRoute>
                    <Navigate to={ROUTES_NAMES.EXPLORER_PANEL} replace />
                  </ExplorerRoute>
                }
              />
              {[
                ROUTES_NAMES.EXPLORER_SOCIAL_LIST_LEGACY,
                ROUTES_NAMES.EXPLORER_SOCIAL_CHANNEL_LEGACY,
              ].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ExplorerRoute>
                      <LegacyExplorerSocialRedirect />
                    </ExplorerRoute>
                  }
                />
              ))}
              {[
                ROUTES_NAMES.EXPLORER_PANEL,
                ROUTES_NAMES.EXPLORER_BRANDS_LIST,
                ROUTES_NAMES.EXPLORER_CANALES,
                ROUTES_NAMES.EXPLORER_PROGRAMS_LIST,
                ROUTES_NAMES.EXPLORER_SOCIAL_LIST,
                ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST,
              ].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ExplorerRoute>
                      <Suspense
                        fallback={<Loading message="Cargando Explorador..." />}
                      >
                        <ExplorerProduct />
                      </Suspense>
                    </ExplorerRoute>
                  }
                />
              ))}
              <Route
                path={ROUTES_NAMES.EXPLORER_MIS_PROGRAMAS}
                element={
                  <ExplorerRoute>
                    <Suspense
                      fallback={<Loading message="Cargando Explorador..." />}
                    >
                      <ExplorerMisProgramasPage />
                    </Suspense>
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_MIS_PROGRAMAS_NUEVO}
                element={
                  <ExplorerRoute>
                    <Suspense
                      fallback={<Loading message="Cargando Explorador..." />}
                    >
                      <CreateExplorerProgramPage />
                    </Suspense>
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_MIS_PROGRAMAS_EDITAR}
                element={
                  <ExplorerRoute>
                    <Suspense
                      fallback={<Loading message="Cargando Explorador..." />}
                    >
                      <CreateExplorerProgramPage />
                    </Suspense>
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_FOLLOWED_PROGRAM}
                element={
                  <ExplorerRoute>
                    <Suspense
                      fallback={<Loading message="Cargando Explorador..." />}
                    >
                      <ExplorerFollowedProgramFeedPage />
                    </Suspense>
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_PROGRAM}
                element={
                  <ExplorerRoute>
                    <ExplorerProgramDetail />
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_PODCAST}
                element={
                  <ExplorerRoute>
                    <ExplorerPodcastDetail />
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_SOCIAL_BRAND}
                element={
                  <ExplorerRoute>
                    <ExplorerSocialBrandDetail />
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_SOCIAL_CHANNEL}
                element={
                  <ExplorerRoute>
                    <ExplorerSocialChannelDetail />
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_LIVESTREAM_PROGRAM}
                element={
                  <ExplorerRoute>
                    <ExplorerLivestreamProgramDetail />
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_LIVESTREAM_AUDIENCIA}
                element={
                  <ExplorerRoute>
                    <LivestreamsHeatmapRedirect />
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_LIVESTREAM_COMPETITION}
                element={
                  <ExplorerRoute>
                    <Suspense fallback={<Loading message="Cargando Explorador..." />}>
                      <ExplorerProduct />
                    </Suspense>
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_LIVESTREAM_CHANNEL_PROGRAM}
                element={
                  <ExplorerRoute>
                    <ExplorerLivestreamProgramDetail />
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_LIVESTREAM_CHANNEL}
                element={
                  <ExplorerRoute>
                    <ExplorerLivestreamChannelDetail />
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_BRAND_MOVERS}
                element={
                  <ExplorerRoute>
                    <ExplorerBrandMovers />
                  </ExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.EXPLORER_BRAND}
                element={
                  <ExplorerRoute>
                    <ExplorerBrandDetail />
                  </ExplorerRoute>
                }
              />

              {/* Tracker - Brand Tracking */}
              <Route
                path={ROUTES_NAMES.TRACKER}
                element={
                  <TrackerRoute>
                    <BrandExplorerYear />
                  </TrackerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.TRACKER_LIVESTREAM_PROGRAM}
                element={
                  <TrackerRoute>
                    <TrackerLivestreamProgramDetail />
                  </TrackerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.TRACKER_PROGRAM}
                element={
                  <TrackerRoute>
                    <BrandExplorerProgramDetail />
                  </TrackerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.TRACKER_YOUTUBE_SHORTS_CREATOR}
                element={
                  <TrackerRoute>
                    <BrandExplorerYoutubeShortsCreatorDetail />
                  </TrackerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.TRACKER_SHORTFORM_PLATFORM}
                element={
                  <TrackerRoute>
                    <BrandExplorerShortFormPlatformDetail />
                  </TrackerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.TRACKER_SHORTFORM_CONTENT}
                element={
                  <TrackerRoute>
                    <BrandExplorerContentDetail />
                  </TrackerRoute>
                }
              />
              <Route
                path="/tracker/creators/:creatorId"
                element={<Navigate to={ROUTES_NAMES.TRACKER} replace />}
              />
              <Route
                path={ROUTES_NAMES.TRACKER_CREATOR}
                element={
                  <TrackerRoute>
                    <BrandExplorerCreatorDetail />
                  </TrackerRoute>
                }
              />

              {/* TonyBet Explorer - Public routes mirror tracker (same paths, fixed brand, no auth) */}
              <Route
                path={ROUTES_NAMES.TONYBET_EXPLORER_YEAR}
                element={
                  <PublicTonyBetExplorerRoute>
                    <BrandExplorerYear fixedBrandId="68ab4884dfd4c581cbe669bb" />
                  </PublicTonyBetExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.TONYBET_EXPLORER_PROGRAM}
                element={
                  <PublicTonyBetExplorerRoute>
                    <BrandExplorerProgramDetail fixedBrandId="68ab4884dfd4c581cbe669bb" />
                  </PublicTonyBetExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.TONYBET_SHORTFORM_PLATFORM}
                element={
                  <PublicTonyBetExplorerRoute>
                    <BrandExplorerShortFormPlatformDetail fixedBrandId="68ab4884dfd4c581cbe669bb" />
                  </PublicTonyBetExplorerRoute>
                }
              />
              <Route
                path={ROUTES_NAMES.TONYBET_SHORTFORM_CONTENT}
                element={
                  <PublicTonyBetExplorerRoute>
                    <BrandExplorerContentDetail fixedBrandId="68ab4884dfd4c581cbe669bb" />
                  </PublicTonyBetExplorerRoute>
                }
              />
              <Route
                path="/tonybet/creators/:creatorId"
                element={
                  <Navigate to={ROUTES_NAMES.TONYBET_EXPLORER_YEAR} replace />
                }
              />
              <Route
                path={ROUTES_NAMES.TONYBET_EXPLORER_CREATOR}
                element={
                  <PublicTonyBetExplorerRoute>
                    <BrandExplorerCreatorDetail fixedBrandId="68ab4884dfd4c581cbe669bb" />
                  </PublicTonyBetExplorerRoute>
                }
              />

              {/* Legal Pages - Public Access */}
              <Route path={ROUTES_NAMES.PRIVACY} element={<PrivacyPolicy />} />
            <Route path={ROUTES_NAMES.TERMS} element={<TermsOfService />} />

            {/* Brand Qualitative Report - Public Access (no auth) */}
              <Route
                path={ROUTES_NAMES.BRAND_QUALITATIVE_REPORT}
                element={<BrandQualitativeReport />}
              />

              {/* Catch-all route for any non-existent routes */}
              <Route
                path="*"
                element={
                  isAuthenticated ? (
                    <AuthenticatedDefaultRedirect />
                  ) : (
                    <Navigate to={ROUTES_NAMES.AUTH} replace />
                  )
                }
              />
            </Routes>
          )}
        </Box>
        {/* Show footer when not authenticated (but not on sponsor links or media kit), on legal/elecciones, or auth-layout routes (/auth*, podcast invite) */}
        {((!isAuthenticated &&
          !isLoading &&
          !isPublicSponsorLink &&
          !isPublicCollectionView) ||
          shouldShowFooter ||
          isAuthLayoutShell) && (
          <Footer
            compact={isAuthLayoutShell}
            alignWithLandingNavbar={isAuthLayoutShell}
          />
        )}
      </Box>
    </Box>
  );

  if (isEleccionesPage) {
    return <ThemeProvider theme={eleccionesTheme}>{layoutTree}</ThemeProvider>;
  }

  return layoutTree;
};

export default Layout;
export { ROUTES_NAMES } from "./routes";
